import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import { type CaseOut, updateLabelMutation } from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  Textarea,
  Typography,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { LABEL_MAX_LENGTH } from "../cases/caseStates";

import type { ReactNode } from "react";

export const CASE_LABEL_TEST_ID = "golden-case-label";

const labelSchema = z.object({
  label: z.string().trim().max(LABEL_MAX_LENGTH),
});

type LabelInput = z.input<typeof labelSchema>;
type LabelValues = z.output<typeof labelSchema>;

/** The case's label: what its RFP tests, so a reviewer can pick cases by it. */
export const CaseLabelForm = ({
  goldenCase,
}: {
  readonly goldenCase: CaseOut;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const { control, handleSubmit } = useForm<LabelInput, unknown, LabelValues>({
    resolver: zodResolver(labelSchema),
    defaultValues: { label: goldenCase.label ?? "" },
  });
  const saveLabel = useMutation({
    ...updateLabelMutation({ client }),
    onSuccess: () => invalidateQuery({ tags: [...GOLDEN_TAGS.cases] }),
  });

  const onSubmit = handleSubmit(({ label }) =>
    saveLabel.mutate({
      path: { case_id: goldenCase.id },
      body: { label: label || null },
    }),
  );

  return (
    <Card data-testid={CASE_LABEL_TEST_ID} title="Case label">
      <Box
        component="form"
        noValidate
        onSubmit={(event) => void onSubmit(event)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {saveLabel.isSuccess && (
          <Alert data-testid={`${CASE_LABEL_TEST_ID}-saved`} severity="success">
            Label saved.
          </Alert>
        )}
        {saveLabel.isError && (
          <Alert data-testid={`${CASE_LABEL_TEST_ID}-error`} severity="error">
            {extractErrorMessage(saveLabel.error, "Failed to save the label")}
          </Alert>
        )}
        {/* The design-system field forwards its ref to the wrapper, not the input,
            so it is controlled rather than registered. */}
        <Controller
          control={control}
          name="label"
          render={({ field: { ref: _ref, ...field } }) => (
            <Textarea
              data-testid={`${CASE_LABEL_TEST_ID}-input`}
              label="What this RFP tests"
              // The design system's 80px minimum also lands on MUI's hidden measuring
              // textarea, so each row measures 80px; one row is the minimum, about three lines.
              rows={1}
              maxLength={LABEL_MAX_LENGTH}
              {...field}
            />
          )}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            type="submit"
            data-testid={`${CASE_LABEL_TEST_ID}-save`}
            loading={saveLabel.isPending}
          >
            Save label
          </Button>
          {goldenCase.label_updated_by && (
            <Typography variant="body-medium" color="textSecondary">
              Last changed by {goldenCase.label_updated_by}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};
